import {
  Box,
  Button,
  Icon,
  IconButton,
  Theme,
  Typography,
} from "@outschool/backpack";
import { faBars, faChevronLeft, faComment } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { LearnerRoutes } from "@outschool/routes";
import { OUTSCHOOL_TIMEZONE, dayjs } from "@outschool/time";
import { ReadOnlyBanner } from "@outschool/ui-components-website";
import { useLinkComponent } from "@outschool/ui-utils";
import React from "react";

import * as Env from "../../Env";
import { useCurrentLearner } from "../../providers/CurrentLearnerProvider";
import { useLearnerClassroomContext } from "../../providers/LearnerClassroomProvider";
import { useLessonsNavDrawer } from "../../providers/LessonsNavDrawerProvider";

export default function ClassroomHeaderV2() {
  const Link = useLinkComponent();
  const { t } = useTranslation("learnerApp\\LearnerClassroom");
  const currentLearner = useCurrentLearner();
  const { openNavDrawer } = useLessonsNavDrawer();
  const { loading, error, activity, section } = useLearnerClassroomContext();

  if (loading || error || !activity || !section) {
    return null;
  }

  const userTimeZone = currentLearner?.timeZone || OUTSCHOOL_TIMEZONE;
  const endTime = section.end_time
    ? dayjs(section.end_time).tz(userTimeZone)
    : null;

  return (
    <Box
      data-test-id="class-header-v2"
      sx={(theme: Theme) => ({
        paddingLeft: 72,
        paddingRight: 8,
        paddingY: 22,
        [theme.breakpoints.down("md")]: {
          padding: 0,
        },
      })}
    >
      {Env.IS_READ_ONLY_MODE && <ReadOnlyBanner />}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          color: "grey.900",
        }}
      >
        <Link
          to={LearnerRoutes.dashboardPath()}
          style={{
            textDecoration: "none",
            display: "flex",
            alignItems: "center",
            gap: 6,
            color: "inherit",
          }}
        >
          <Icon icon={faChevronLeft} sx={{ fontSize: 14 }} />
          <Typography variant="subtitle2" sx={{ letterSpacing: "0.1px" }}>
            {t("Back to all classes")}
          </Typography>
        </Link>

        <Box
          sx={(theme: Theme) => ({
            display: "block",
            [theme.breakpoints.up("md")]: {
              display: "none",
            },
          })}
        >
          <IconButton
            icon={faBars}
            variant="text"
            onClick={openNavDrawer}
            aria-label={t("Open lessons menu")}
          />
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "7px",
            minWidth: 0,
          }}
        >
          <Typography
            variant="h2"
            sx={{
              letterSpacing: "-0.4px",
              color: "grey.900",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {activity.title}
          </Typography>

          {endTime && (
            <Typography
              variant="body1"
              sx={(theme: Theme) => ({
                marginBottom: "10px",
                color: "grey.600",
                [theme.breakpoints.down("md")]: {
                  display: "none",
                },
              })}
            >
              {t("Class access ends {{date}}", {
                date: endTime.format("MMM D, YYYY"),
              })}
            </Typography>
          )}
        </Box>

        <Button
          variant="contained"
          sx={(theme: Theme) => ({
            whiteSpace: "nowrap",
            borderRadius: "9999px",
            textTransform: "none",
            fontSize: 18,
            fontWeight: 500,
            width: 200,
            height: 48,
            boxShadow: "0px 0px 2px #6161F2",
            [theme.breakpoints.down("md")]: {
              display: "none",
            },
          })}
          onClick={() => {
            // TODO @Jaewon: Add logic to open the new messaging feature once ready
          }}
        >
          {t("Message teacher")}
        </Button>

        <IconButton
          icon={faComment}
          variant="contained"
          sx={(theme: Theme) => ({
            display: "none",
            [theme.breakpoints.down("md")]: {
              display: "inline-flex",
            },
          })}
          onClick={() => {
            // TODO @Jaewon: Add logic to open the new messaging feature once ready
          }}
          aria-label={t("Message teacher")}
        />
      </Box>
    </Box>
  );
}
